import { useMemo, useState } from "react";
import {
  clearAccessPageStateCookie,
  loadAccessPageStateCookie,
  saveAccessPageStateCookie,
} from "../utils/accessPageStateCookie";

const DEFAULT_TABLE_PAGINATION = { current: 1, pageSize: 10 };

const normalizeTablePagination = (
  pagination,
  fallback = DEFAULT_TABLE_PAGINATION
) => ({
  current: pagination?.current ?? fallback.current,
  pageSize: pagination?.pageSize ?? fallback.pageSize,
});

const buildPageCookieKey = () => {
  const pathname = window.location.pathname || "access-page";

  return (
    pathname.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").toLowerCase() ||
    "access_page"
  );
};

const normalizeRestoredValues = (restored) => restored?.searchValues ?? restored ?? null;

const canUseForcedSearchValues = (value) => {
  if (!value || Array.isArray(value) || typeof value !== "object") {
    return false;
  }

  return !(
    "target" in value ||
    "nativeEvent" in value ||
    typeof value.preventDefault === "function"
  );
};

const useAccessPageState = ({
  form,
  defaultTablePagination = DEFAULT_TABLE_PAGINATION,
  persistPagination = true,
}) => {
  const pageCookieKey = useMemo(() => buildPageCookieKey(), []);
  const normalizedDefaultPagination = useMemo(
    () => normalizeTablePagination(defaultTablePagination, DEFAULT_TABLE_PAGINATION),
    [defaultTablePagination]
  );
  const [tablePagination, setTablePagination] = useState(normalizedDefaultPagination);

  const saveState = (searchValues, pagination = tablePagination) => {
    const payload = { searchValues };

    if (persistPagination) {
      payload.tablePagination = normalizeTablePagination(
        pagination,
        normalizedDefaultPagination
      );
    }

    saveAccessPageStateCookie(pageCookieKey, payload);
  };

  const restoreState = () => {
    const restored = loadAccessPageStateCookie(pageCookieKey);
    const searchValues = normalizeRestoredValues(restored);
    const nextPagination = persistPagination
      ? normalizeTablePagination(restored?.tablePagination, normalizedDefaultPagination)
      : normalizedDefaultPagination;

    if (searchValues) {
      form.setFieldsValue(searchValues);
    }

    if (persistPagination) {
      setTablePagination(nextPagination);
    }

    return {
      searchValues,
      tablePagination: nextPagination,
    };
  };

  const clearState = () => {
    clearAccessPageStateCookie(pageCookieKey);
  };

  const restoreDefaultPagination = () => {
    setTablePagination(normalizedDefaultPagination);
    return normalizedDefaultPagination;
  };

  const handleTableChange = (pagination) => {
    if (!persistPagination) {
      return;
    }

    const nextPagination = normalizeTablePagination(
      pagination,
      normalizedDefaultPagination
    );

    setTablePagination(nextPagination);
    saveState(form.getFieldsValue(true), nextPagination);
  };

  return {
    tablePagination,
    saveState,
    restoreState,
    clearState,
    restoreDefaultPagination,
    handleTableChange,
  };
};

useAccessPageState.canUseForcedSearchValues = canUseForcedSearchValues;

export default useAccessPageState;